import React, { useState } from "react";
import { ChevronDown, SearchIcon } from "lucide-react";
import { Button } from "./button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from "./dialog";

/**
 * CustomDropdown - A reusable dropdown component with search functionality
 *
 * @param {Object} props
 * @param {Array} props.items - Array of items to display in dropdown
 * @param {string|Object} props.selectedItem - Currently selected item
 * @param {Function} props.onSelect - Callback when item is selected (item) => void
 * @param {Function} props.renderItem - Custom render function for each item (item, isSelected) => ReactNode
 * @param {Function} props.renderTrigger - Custom render function for trigger button (selectedItem) => ReactNode
 * @param {Function} props.filterItems - Custom filter function (items, searchTerm) => filteredItems
 * @param {string} props.placeholder - Placeholder text for search input
 * @param {string} props.title - Dialog title
 * @param {string} props.description - Dialog description
 * @param {string} props.triggerClassName - Additional classes for trigger button
 * @param {boolean} props.searchEnabled - Enable/disable search (default: true)
 * @param {boolean} props.fullWidth - Make trigger button full width (default: false)
 */
const CustomDropdown = ({
  items = [],
  selectedItem,
  onSelect,
  renderItem,
  renderTrigger,
  filterItems,
  placeholder = "Search...",
  title = "Select an option",
  description,
  triggerClassName = "",
  searchEnabled = true,
  fullWidth = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  // Default filter function if not provided
  const defaultFilterItems = (items, term) => {
    if (!term) return items;
    return items.filter((item) => {
      const searchableText =
        typeof item === "string" ? item : JSON.stringify(Object.values(item));
      return searchableText.toLowerCase().includes(term.toLowerCase());
    });
  };

  const filteredItems = filterItems
    ? filterItems(items, searchTerm)
    : defaultFilterItems(items, searchTerm);

  // Default render functions
  const defaultRenderItem = (item, isSelected) => {
    const displayText =
      typeof item === "string" ? item : item.name || item.label || String(item);
    return (
      <div
        className={`flex items-center justify-between px-4 py-3 cursor-pointer border rounded-md mb-2 ${
          isSelected ? "border-rose-300 bg-rose-50" : "border-gray-200"
        } hover:bg-gray-100`}
      >
        <span className="text-sm font-medium text-gray-800">{displayText}</span>
      </div>
    );
  };

  const defaultRenderTrigger = (item) => {
    const displayText = item
      ? typeof item === "string"
        ? item
        : item.name || item.label || String(item)
      : "Select...";

    return (
      <>
        <span className="text-sm font-semibold">{displayText}</span>
        <ChevronDown />
      </>
    );
  };

  const handleSelect = (item) => {
    if (onSelect) {
      onSelect(item);
    }
    setSearchTerm(""); // Reset search on selection
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          className={`relative h-10 rounded-full bg-transparent backdrop-blur-sm border border-gray-100 border-opacity-35 text-gray-600 flex justify-between px-4 ${
            fullWidth ? "w-full" : ""
          } ${triggerClassName}`}
        >
          {renderTrigger
            ? renderTrigger(selectedItem)
            : defaultRenderTrigger(selectedItem)}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md p-6 rounded-lg shadow-lg">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-gray-700 mt-5">
            {title}
          </DialogTitle>
          {description && (
            <p className="text-sm text-gray-500">{description}</p>
          )}
        </DialogHeader>

        {searchEnabled && (
          <div className="relative mt-4 flex space-x-2 items-center">
            <input
              type="text"
              placeholder={placeholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-600 focus:outline-none"
            />
            <SearchIcon className="text-gray-400" size={16} />
          </div>
        )}

        <div className="mt-4 max-h-[400px] overflow-y-auto">
          <DialogClose className="w-full">
            {filteredItems.map((item, index) => {
              const itemKey =
                typeof item === "object" && item._id
                  ? item._id
                  : typeof item === "object" && item.id
                    ? item.id
                    : index;

              const isSelected =
                typeof selectedItem === "object" && typeof item === "object"
                  ? selectedItem?.id === item?.id ||
                    selectedItem?._id === item?._id
                  : selectedItem === item;

              return (
                <div key={itemKey} onClick={() => handleSelect(item)}>
                  {renderItem
                    ? renderItem(item, isSelected)
                    : defaultRenderItem(item, isSelected)}
                </div>
              );
            })}
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CustomDropdown;
